<script setup lang="ts">
/**
 * Reject a withdraw request — operator provides a free-text rejection
 * reason (min 5 characters per BE contract) that is persisted on the
 * request and surfaced back to the investor's wallet UI.
 */
import { computed, ref, watch } from 'vue'
import Dialog from '@uiKit/elements/Dialog.vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import Button from '@uiKit/elements/Button.vue'
import Textarea from '@uiKit/elements/Textarea.vue'
import Message from '@uiKit/elements/Message.vue'
import FormField from '@uiKit/components/FormField.vue'

interface Props {
  visible: boolean
  loading?: boolean
  apiError?: string | null
}

const MIN_REASON_LENGTH = 5

const props = withDefaults(defineProps<Props>(), {
  loading: false,
  apiError: null,
})

const emit = defineEmits<{
  (e: 'update:visible', value: boolean): void
  (e: 'confirm', value: { reason: string }): void
}>()

const reason = ref('')
const localError = ref('')

watch(
  () => props.visible,
  (isVisible) => {
    if (!isVisible) {
      reason.value = ''
      localError.value = ''
    }
  },
)

const trimmedReason = computed(() => reason.value.trim())

function handleConfirm() {
  if (trimmedReason.value.length < MIN_REASON_LENGTH) {
    localError.value = `Reason must be at least ${MIN_REASON_LENGTH} characters.`
    return
  }
  localError.value = ''
  emit('confirm', { reason: trimmedReason.value })
}

function handleInput(value: string) {
  reason.value = value
  if (localError.value) localError.value = ''
}

function handleClose() {
  if (props.loading) return
  emit('update:visible', false)
}
</script>

<template>
  <Dialog
    :visible="visible"
    width="520px"
    max-height="80vh"
    :show-close-icon="!loading"
    :dismissable="!loading"
    @update:visible="(v) => !v && handleClose()"
  >
    <template #header>
      <Text type="page-title">Reject withdraw request</Text>
    </template>

    <Box flex direction="col" gap="3">
      <Text type="body-sm" color="muted">
        The investor will be notified and the locked balance will be returned to their wallet.
        Provide a clear, specific reason — it is shown to the investor verbatim.
      </Text>

      <FormField label="Rejection reason" required :error="localError">
        <Textarea
          :model-value="reason"
          :rows="4"
          placeholder="e.g. KYC mismatch on destination bank account holder name."
          :disabled="loading"
          @update:model-value="handleInput"
        />
      </FormField>

      <Message v-if="apiError" color="danger" variant="outlined" size="small" :message="apiError" />
    </Box>

    <template #footer>
      <Box flex justify="end" gap="2">
        <Button
          label="Cancel"
          variant="text"
          color="secondary"
          :disabled="loading"
          @click="handleClose"
        />
        <Button label="Reject request" color="danger" :loading="loading" @click="handleConfirm" />
      </Box>
    </template>
  </Dialog>
</template>
